<script setup>
import IndexNavbar from "@/components/Navbars/IndexNavbar.vue";
import FooterComponent from "@/components/Footers/Footer.vue";
import {onMounted, ref, watch, nextTick, inject, computed} from "vue";
import {useRoute, useRouter, onBeforeRouteUpdate, onBeforeRouteLeave} from 'vue-router'
import Plotly from 'plotly.js-dist'
import {ElLoading} from 'element-plus'
import {ArrowRight} from '@element-plus/icons-vue'
//分页器

// 默认显示第几页
const currentPage = ref(1)
// 个数选择器（可修改）
let pageSizes = [10, 20, 50, 100, 200, 300, 400]
// 默认每页显示的条数（可修改）
const PageSize = ref(10)

// 显示第几页
function handleCurrentChange(val) {
  // 改变默认的页数
  currentPage.value = val
}

function handleSizeChange(val) {
  // 改变每页显示的条数
  PageSize.value = val
  // 注意：在改变每页显示的条数时，要将页码显示到第一页
  currentPage.value = 1
}

const $axios = inject('$axios')

/* ===== 状态 ===== */
const showExpressionPanels = ref(false)
const activePanels = ref(['umap', 'Plot', 'Violin'])
const activeTab = ref('overview')

const sample = ref('')
const inputText = ref('')

function inputInput(e) {
  inputText.value = e.target.value;
}


/* ===== 路由 ===== */
let route = useRoute()
let router = useRouter()

if (!route.query.sample) router.replace({path: '/'})

onBeforeRouteUpdate((to, from, next) => {
  if (!to.query.sample) {
    router.replace({
      ...to,
      query: {...to.query, sample: from.query.sample || 'default'}
    })
  } else next()
})

onBeforeRouteLeave((to, from, next) => next())

/* ===== 输入 ===== */
const umapSrc = computed(() => {
  return `/PNSCA/${sample.value}/5.annot/merge_umap_annot.png`
})
const umapSrc2 = computed(() => {
  return `/PNSCA/${sample.value}/5.annot/merge_umap_cluster_annot.png`
})
const featureSrc = computed(() => {
  return `/PNSCA/${sample.value}/2.markgene/featureplot/featureplot_${markerCluster.value}.png`
})
const violinSrc = computed(() => {
  return `/PNSCA/${sample.value}/2.markgene/violin/violin_${markerCluster.value}.png`
})
const heatmapSrc = computed(() => {
  return `/PNSCA/${sample.value}/2.markgene/heatmap/top10_heatmap.png`
})
const volcanoSrc = computed(() => {
  return `/PNSCA/${sample.value}/4.diff_enrich/diff/volcano/${cluster.value}_volcano.png`
})
const enrichSrc = computed(() => {
  return `/PNSCA/${sample.value}/4.diff_enrich/enrich/${upordown.value}/${cluster.value}/${cluster.value}_${upordown.value.toLowerCase()}_Enrichment${goType.value.replace('GO.', '')}.bar.png`
})
const annoSrc = computed(() => {
  return `/PNSCA/${sample.value}/5.annot/${cellType.value}_featureplot.png`
})
const annoBub = computed(() => {
  return `/PNSCA/${sample.value}/5.annot/genelist_dotplot.png`
})
const cluster = ref('cluster0')
const upordown = ref('UP')
const goType = ref('GO.BP')
const cellType = ref('NK-cell')
/* ===== Resize修复 ===== */
watch(showExpressionPanels, async (visible) => {
  if (visible) {
    await nextTick()
    Plotly.Plots.resize(document.getElementById('geneUmapPlot'))
    Plotly.Plots.resize(document.getElementById('boxPlot'))
  }
})

window.addEventListener('resize', () => {
  setTimeout(() => {
    Plotly.Plots.resize(document.getElementById('umapPlot'))
  }, 200)
})

/* ===== UMAP ===== */
async function drawUmapAnnotation(tissue) {
  const loading = ElLoading.service({fullscreen: true})
  try {
    const res = await $axios.get(`http://bioinfo-sysu.com:8282/api/get_umap_annotation?tissue=${tissue}`)
    const data = res.data

    const categoryKey = tissue === 'Blood' ? 'sub_celltype' : 'major_celltype'
    const categories = [...new Set(data.map(d => d[categoryKey]))]

    const traces = categories.map(type => {
      const subset = data.filter(d => d[categoryKey] === type)
      return {
        x: subset.map(d => d.UMAP_1),
        y: subset.map(d => d.UMAP_2),
        mode: 'markers',
        type: 'scattergl',
        name: type,
        marker: {size: 5}
      }
    })

    Plotly.newPlot('umapPlot', traces, {title: `UMAP — ${tissue}`})
  } finally {
    loading.close()
  }
}

/* ===== 表达 ===== */
async function drawUmapExpression(sample, gene) {
  const loading = ElLoading.service({ fullscreen: true })
  if (!gene) gene = 'CD3D'

  try {
    const res = await $axios.get(
      `http://bioinfo-sysu.com:8585/api/get_umap_expression?sample=${sample}&gene=${gene}`
    )
    const data = res.data

    Plotly.newPlot('geneUmapPlot', [{
      x: data.map(d => d.UMAP_1),   // ✅ 改成大写
      y: data.map(d => d.UMAP_2),   // ✅ 改成大写
      mode: 'markers',
      type: 'scattergl',
      customdata: data.map(d => ([
        d.cell_id,
        d.majortype,
        d.expr,
        d.Feature,
        d.group,
        d.sample
      ])),
      hovertemplate:
        "cell_id: %{customdata[0]}<<br>" +
        "majortype: %{customdata[1]}<<br>" +
        "expr: %{customdata[2]:.3f}<br>" +
        "Feature: %{customdata[3]}<<br>" +
        "group: %{customdata[4]}<<br>" +
        "sample: %{customdata[5]}<<br>" +
        "<extra></extra>",
      marker: {
        color: data.map(d => d.expr),
        colorscale: 'Viridis',
        showscale: true,
        size: 6
      },
    }], {
      title: `Gene Expression: ${gene}`,
      xaxis: { title: 'UMAP 1' },
      yaxis: { title: 'UMAP 2' }
    }, {
      responsive: true
    })

  } catch (err) {
    console.error(err)
    ElMessage.error('表达量数据加载失败')
  } finally {
    loading.close()
  }
}

/* ===== 搜索 ===== */
async function handleSearch() {
  if (!inputText.value) inputText.value = 'CD3D'

  // 1. 先显示面板（关键！）
  showExpressionPanels.value = true

  // 2. 等 Vue 把 DOM 渲染出来，容器有实际宽高后再画图
  await nextTick()

  // 3. 再调用 Plotly
  await drawUmapExpression(sample.value, inputText.value)
}

/* ===== 初始化 ===== */
onMounted(() => {
  if (route.query.sample) {
    sample.value = route.query.sample
    // drawUmapAnnotation(sample.value)
  }
  markerSearch()
  degSearch()
  overview()
  enrichSearch()
})
/* ===== Marker gene ===== */
const markerList = ref([])
const markerGene = ref('')
const markerCluster = ref('cluster0')

function markerInput(e) {
  markerGene.value = e.target.value;
  markerSearch()
}

const loadtable = ref(false)

function markerSearch() {
  loadtable.value = true
  $axios.get('http://bioinfo-sysu.com:8585/api/markerSearch?markergene=' + markerGene.value + '&sample=' + sample.value + '&cluster=' + markerCluster.value).then((resp) => {
    if (JSON.parse(JSON.stringify(resp.data)).respCode === '000000') {
      markerList.value = JSON.parse(JSON.stringify(resp.data))['list']
    }
    loadtable.value = false
  }).catch((err) => {
    loadtable.value = false
    console.log(err)
  })
}


/* ===== type ===== */
const anatype = ref('Cluster')

function typechange() {
  if (anatype.value == 'Cluster') {
    cluster.value = 'cluster0'
  } else {
    cluster.value = 'B-cell'
  }
  degSearch()
}

/* ===== DEG ===== */
const degList = ref([])
const degGene = ref('')
let rawDegList = []  // 👈 不响应式，存原始数据
function degInput(e) {
  degGene.value = e.target.value

  const keyword = degGene.value.toLowerCase()

  if (!keyword) {
    degList.value = rawDegList
    return
  }

  degList.value = rawDegList.filter(item =>
      item.gene?.toLowerCase().includes(keyword)
  )

  currentPage.value = 1 // 👈 搜索后回第一页
}

const loadtable1 = ref(false)

async function degSearch() {
  loadtable1.value = true

  const url = `/PNSCA/${sample.value}/4.diff_enrich/diff/${cluster.value}_sig_diffgene.xls`

  const res = await fetch(url)
  const text = await res.text()

  const lines = text.trim().split('\n')
  const headers = lines[0].split('\t')

  const data = lines.slice(1).map(line => {
    const values = line.split('\t')
    let obj = {}
    headers.forEach((h, i) => obj[h] = values[i])
    return obj
  })

  rawDegList = data        // 👈 存原始
  degList.value = data     // 👈 初始化显示

  loadtable1.value = false
}

/* ===== Overview ===== */
const overviewList = ref([])
const loadtable2 = ref(false)

function overview() {
  loadtable2.value = true
  $axios.get('http://bioinfo-sysu.com:8585/api/get_majortype_summary?sample=' + sample.value).then((resp) => {
    overviewList.value = JSON.parse(JSON.stringify(resp.data))
    loadtable2.value = false
  }).catch((err) => {
    loadtable2.value = false
    console.log(err)
  })
}

/* ===== Enrich ===== */
const enrichList = ref([])

const loadtable3 = ref(false)

async function enrichSearch() {
  loadtable3.value = true

  const url = `/PNSCA/${sample.value}/4.diff_enrich/enrich/${upordown.value}/${cluster.value}/${cluster.value}_${upordown.value.toLowerCase()}_${goType.value}.txt`

  const res = await fetch(url)
  const text = await res.text()

  // 解析 txt（假设 tab 分隔）
  const lines = text.trim().split('\n')
  const headers = lines[0].split('\t')

  const data = lines.slice(1).map(line => {
    const values = line.split('\t')
    let obj = {}
    headers.forEach((h, i) => obj[h] = values[i])
    return obj
  })

  enrichList.value = data
  loadtable3.value = false
}


const clusterOptions = computed(() => {
  const map = {
    Cellsystem: 8,
    GSE171381_Dec: 18,
    GSE171381_Vil: 24,
    GSE174399: 27,
    GSE183338_Dec: 14,
    GSE183338_Vil: 13,
    GSE183837: 14,
    GSE250130: 17,
    GSE290578: 25,
    GSE305088: 20,
    GSE314816: 20,
    HRA005072: 20,
    JustMoveOnnn_Dec: 11,
    JustMoveOnnn_Pla: 9,
    Med_stroma: 9,
    Med_TB: 8,
    merge_rds: 28,
    PRJNA949258: 20,
  }

  const n = map[sample.value] || 0
  return Array.from({length: n + 1}, (_, i) => ({
    value: `cluster${i}`,  // 👈 真正提交的值
    label: i               // 👈 显示的值
  }))
})
</script>

<template>
  <div>
    <index-navbar/>

    <section class="main-section">
      <div class="container-fluid custom-container">

        <!-- breadcrumb -->
        <el-breadcrumb :separator-icon="ArrowRight" class="breadcrumb">
          <el-breadcrumb-item :to="{ path: '/' }">Home</el-breadcrumb-item>
          <el-breadcrumb-item>Single Cell</el-breadcrumb-item>
          <el-breadcrumb-item>{{ sample }}</el-breadcrumb-item>
        </el-breadcrumb>

        <div class="row g-2 custom-row">

          <!-- 左侧 -->
          <div class="col-6">
            <div class="card">
              <el-image
                  :src="umapSrc2"
                  :preview-src-list="[umapSrc2]"
                  fit="contain"
                  style="width: 100%; cursor: zoom-in"
              />
              <el-image
                  :src="umapSrc"
                  :preview-src-list="[umapSrc]"
                  fit="contain"
                  style="width: 100%; cursor: zoom-in"
              />

            </div>
          </div>

          <!-- 右侧 -->
          <div class="col-6">
            <div class="card">

              <el-tabs v-model="activeTab" type="border-card">

                <el-tab-pane label="Overview" name="overview">
                  <h3>Overview</h3>
                  <table class="table table-striped table-bordered table-md align-middle mt-4"
                         style="text-align: left;table-layout:fixed" v-loading="loadtable1">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 25%">Cell Type</th>
                      <th scope="col" style="width: 25%">#Cell</th>
                      <th scope="col" style="width: 25%">#Feature(median)</th>
                      <th scope="col" style="width: 25%">#UMI(median)</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item,index) in overviewList">
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.majortype }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.cell_count }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.avg_Feature }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.avg_UMI }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </el-tab-pane>

                <el-tab-pane label="Marker Table" name="markerTable">
                  <div class="d-flex flex-row justify-content-start align-items-center my-2">
                    <span class="font-bold">Select a cluster:</span>
                    <select class="form-select form-select-lg mx-4" aria-label=".form-select-lg example"
                            v-model="markerCluster"
                            style="width: 30%; color: #ccc;" @change="markerSearch">
                      <option
                          v-for="c in clusterOptions"
                          :key="c.value"
                          :value="c.value"
                          style="color: black;"
                      >
                        {{ c.label }}
                      </option>
                    </select>
                  </div>
                  <table class="table table-striped table-bordered table-md align-middle mt-4"
                         style="text-align: left;table-layout:fixed" v-loading="loadtable">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 15%">Cluster</th>
                      <th scope="col" style="width: 20%">Gene ID</th>
                      <th scope="col" style="width: 15%">log2FC</th>
                      <th scope="col" style="width: 15%">P value</th>
                      <th scope="col" style="width: 15%">adjusted P</th>
                      <th scope="col" style="width: 10%">Pct.1</th>
                      <th scope="col" style="width: 10%">Pct.2</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        <input type="text" placeholder="Search Gene ID" @input="markerInput" :value="markerGene"
                               class="w-full placeholder-blueGray-300 text-blueGray-600 relative bg-white bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline"/>
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                    </tr>
                    <tr v-for="(item,index) in markerList.slice((currentPage-1)*PageSize,currentPage*PageSize)">
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].cluster }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].gene }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].avg_log2fc }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].p_val }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].p_val_adj }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].pct_1 }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['fields'].pct_2 }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <!--    分页操作-->
                  <el-pagination
                      class="el-pager mt-3"
                      style="--el-color-primary:#303034"
                      background
                      @size-change="handleSizeChange"
                      @current-change="handleCurrentChange"
                      :current-page="currentPage"
                      :page-sizes="pageSizes"
                      :page-size="PageSize"
                      layout="total, sizes, prev, pager, next, jumper"
                      :total="markerList.length">
                  </el-pagination>
                </el-tab-pane>

                <el-tab-pane label="Marker Figure" name="markerFigure">
                  <div class="d-flex flex-row justify-content-start align-items-center my-2">
                    <span class="font-bold">Select a cluster:</span>
                    <select class="form-select form-select-lg mx-4" aria-label=".form-select-lg example"
                            v-model="markerCluster"
                            style="width: 20%; color: #ccc;" @change="showRnaseq">
                      <option
                          v-for="c in clusterOptions"
                          :key="c.value"
                          :value="c.value"
                          style="color: black;"
                      >
                        {{ c.label }}
                      </option>
                    </select>
                  </div>
                  <el-image
                      :src="featureSrc"
                      :preview-src-list="[featureSrc]"
                      fit="contain"
                      style="width: 100%; cursor: zoom-in"
                      class="my-4"
                  />
                  <el-image
                      :src="violinSrc"
                      :preview-src-list="[violinSrc]"
                      fit="contain"
                      style="width: 100%; cursor: zoom-in"
                      class="my-4"
                  />
                  <el-image
                      :src="heatmapSrc"
                      :preview-src-list="[heatmapSrc]"
                      fit="contain"
                      style="width: 100%; cursor: zoom-in"
                      class="my-4"
                  />
                </el-tab-pane>

                <el-tab-pane label="Annotation" name="annotation">
<!--                  <div class="d-flex flex-row justify-content-start align-items-center my-2">-->
<!--                    <span class="font-bold">Select the cell type:</span>-->
<!--                    <select class="form-select form-select-lg mx-4" aria-label=".form-select-lg example"-->
<!--                            v-model="cellType"-->
<!--                            style="width: 20%; color: #ccc;" @change="showRnaseq">-->
<!--                      <option-->
<!--                          v-for="(item, index) in overviewList"-->
<!--                          :key="index"-->
<!--                          :value="item.majortype"-->
<!--                          style="color: black;"-->
<!--                      >-->
<!--                        {{ item.majortype }}-->
<!--                      </option>-->
<!--                    </select>-->
<!--                  </div>-->
<!--                  <el-image-->
<!--                      :src="annoSrc"-->
<!--                      :preview-src-list="[annoSrc]"-->
<!--                      fit="contain"-->
<!--                      style="width: 100%; cursor: zoom-in"-->
<!--                      class="my-4"-->
<!--                  />-->
                  <el-image
                      :src="annoBub"
                      :preview-src-list="[annoBub]"
                      fit="contain"
                      style="width: 100%; cursor: zoom-in"
                      class="my-4"
                  />
                </el-tab-pane>

                <el-tab-pane label="DEG" name="deg">
                  <div class="d-flex flex-row justify-content-start align-items-center my-2">
                    <span class="font-bold">By cluster or cell type:</span>
                    <select class="form-select form-select-lg mx-4" aria-label=".form-select-lg example"
                            v-model="anatype"
                            style="width: 30%; color: #ccc;" @change="typechange">
                      <option value="Cluster" style="color: black;">Cluster</option>
                      <option value="CellType" style="color: black;">Cell Type</option>
                    </select>
                    <select v-if="anatype=='Cluster'" class="form-select form-select-lg mx-4"
                            aria-label=".form-select-lg example"
                            v-model="cluster"
                            style="width: 30%; color: #ccc;" @change="degSearch">
                      <option
                          v-for="c in clusterOptions"
                          :key="c.value"
                          :value="c.value"
                          style="color: black;"
                      >
                        {{ c.label }}
                      </option>
                    </select>
                    <select
                        v-if="anatype == 'CellType'"
                        class="form-select form-select-lg mx-4"
                        aria-label=".form-select-lg example"
                        v-model="cluster"
                        style="width: 30%; color: #ccc;"
                        @change="degSearch"
                    >
                      <option
                          v-for="(item, index) in overviewList"
                          :key="index"
                          :value="item.majortype"
                          style="color: black;"
                      >
                        {{ item.majortype }}
                      </option>
                    </select>
                  </div>
                  <table class="table table-striped table-bordered table-md align-middle mt-4"
                         style="text-align: left;table-layout:fixed" v-loading="loadtable1">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 20%">Gene ID</th>
                      <th scope="col" style="width: 20%">log2FC</th>
                      <th scope="col" style="width: 20%">P value</th>
                      <th scope="col" style="width: 20%">adjusted P</th>
                      <th scope="col" style="width: 10%">Pct.1</th>
                      <th scope="col" style="width: 10%">Pct.2</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        <input type="text" placeholder="Search Gene ID" @input="degInput" :value="degGene"
                               class="w-full placeholder-blueGray-300 text-blueGray-600 relative bg-white bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline"/>
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">-
                      </td>
                    </tr>
                    <tr v-for="(item,index) in degList.slice((currentPage-1)*PageSize,currentPage*PageSize)">
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.gene }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.avg_log2FC }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.p_val }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.p_val_adj }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['pct.1'] }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['pct.2'] }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <!--    分页操作-->
                  <el-pagination
                      class="el-pager mt-3"
                      style="--el-color-primary:#303034"
                      background
                      @size-change="handleSizeChange"
                      @current-change="handleCurrentChange"
                      :current-page="currentPage"
                      :page-sizes="pageSizes"
                      :page-size="PageSize"
                      layout="total, sizes, prev, pager, next, jumper"
                      :total="degList.length">
                  </el-pagination>
                  <el-image
                      :src="volcanoSrc"
                      :preview-src-list="[volcanoSrc]"
                      fit="contain"
                      style="width: 100%; cursor: zoom-in"
                      class="my-4"
                  />
                </el-tab-pane>

                <el-tab-pane label="Enrichment" name="enrichment">
                  <div class="d-flex flex-row justify-content-start align-items-center my-2">
                    <span class="font-bold">By cluster or cell type:</span>
                    <select class="form-select form-select-lg mx-4" aria-label=".form-select-lg example"
                            v-model="anatype"
                            style="width: 30%; color: #ccc;" @change="typechange">
                      <option value="Cluster" style="color: black;">Cluster</option>
                      <option value="CellType" style="color: black;">Cell Type</option>
                    </select>
                    <select v-if="anatype=='Cluster'" class="form-select form-select-lg mx-4"
                            aria-label=".form-select-lg example"
                            v-model="cluster"
                            style="width: 20%; color: #ccc;" @change="degSearch">
                      <option
                          v-for="c in clusterOptions"
                          :key="c.value"
                          :value="c.value"
                          style="color: black;"
                      >
                        {{ c.label }}
                      </option>
                    </select>
                    <select
                        v-if="anatype == 'CellType'"
                        class="form-select form-select-lg mx-4"
                        aria-label=".form-select-lg example"
                        v-model="cluster"
                        style="width: 30%; color: #ccc;"
                        @change="degSearch"
                    >
                      <option
                          v-for="(item, index) in overviewList"
                          :key="index"
                          :value="item.majortype"
                          style="color: black;"
                      >
                        {{ item.majortype }}
                      </option>
                    </select>
                    <span class="font-bold">Up/Down:</span>
                    <select class="form-select form-select-lg mx-4" aria-label=".form-select-lg example"
                            v-model="upordown"
                            style="width: 20%; color: #ccc;" @change="enrichSearch">
                      <option value="UP" style="color: black;">Up</option>
                      <option value="DOWN" style="color: black;">Down</option>
                    </select>
                    <span class="font-bold">Type:</span>
                    <select class="form-select form-select-lg mx-4" aria-label=".form-select-lg example"
                            v-model="goType"
                            style="width: 20%; color: #ccc;" @change="enrichSearch">
                      <option value="GO.BP" style="color: black;">GO_BP</option>
                      <option value="GO.MF" style="color: black;">GO_MF</option>
                      <option value="GO.CC" style="color: black;">GO_CC</option>
                      <option value="KEGG" style="color: black;">KEGG</option>
                    </select>
                  </div>
                  <el-image
                      :src="enrichSrc"
                      :preview-src-list="[enrichSrc]"
                      fit="contain"
                      style="width: 100%; cursor: zoom-in"
                      class="my-4"
                  />
                  <table class="table table-striped table-bordered table-md align-middle mt-4"
                         style="text-align: left;table-layout:fixed" v-loading="loadtable3" v-if="goType!='KEGG'">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 15%">ID</th>
                      <th scope="col" style="width: 20%">Description</th>
                      <th scope="col" style="width: 15%">GeneRatio</th>
                      <th scope="col" style="width: 15%">BgRatio</th>
                      <th scope="col" style="width: 10%">Richfactor</th>
                      <th scope="col" style="width: 10%">Foldenrichment</th>
                      <th scope="col" style="width: 10%">P value</th>
                      <th scope="col" style="width: 10%">adjusted P</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item,index) in enrichList.slice((currentPage-1)*PageSize,currentPage*PageSize)">
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.ID }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.Description }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.GeneRatio }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.BgRatio }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.RichFactor }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.FoldEnrichment }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.pvalue }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item['p.adjust'] }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <!--    分页操作-->
                  <el-pagination
                      v-if="goType!='KEGG'"
                      class="el-pager mt-3"
                      style="--el-color-primary:#303034"
                      background
                      @size-change="handleSizeChange"
                      @current-change="handleCurrentChange"
                      :current-page="currentPage"
                      :page-sizes="pageSizes"
                      :page-size="PageSize"
                      layout="total, sizes, prev, pager, next, jumper"
                      :total="enrichList.length">
                  </el-pagination>
                  <table class="table table-striped table-bordered table-md align-middle mt-4"
                         style="text-align: left;table-layout:fixed" v-loading="loadtable3" v-if="goType=='KEGG'">
                    <thead>
                    <tr>
                      <th scope="col" style="width: 10%">Category</th>
                      <th scope="col" style="width: 10%">Subcategory</th>
                      <th scope="col" style="width: 10%">ID</th>
                      <th scope="col" style="width: 10%">Description</th>
                      <th scope="col" style="width: 10%">GeneRatio</th>
                      <th scope="col" style="width: 10%">BgRatio</th>
                      <th scope="col" style="width: 10%">Foldenrichment</th>
                      <th scope="col" style="width: 30%">GeneName</th>
                      <th scope="col" style="width: 10%">zScore</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item,index) in enrichList.slice((currentPage-1)*PageSize,currentPage*PageSize)">
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.category }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.subcategory }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.ID }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.Description }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.GeneRatio }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.BgRatio }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.FoldEnrichment }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.geneName }}
                      </td>
                      <td style="word-break:break-all; word-wrap:break-word; white-space:inherit">
                        {{ item.zScore }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                  <!--    分页操作-->
                  <el-pagination
                      v-if="goType=='KEGG'"
                      class="el-pager mt-3"
                      style="--el-color-primary:#303034"
                      background
                      @size-change="handleSizeChange"
                      @current-change="handleCurrentChange"
                      :current-page="currentPage"
                      :page-sizes="pageSizes"
                      :page-size="PageSize"
                      layout="total, sizes, prev, pager, next, jumper"
                      :total="enrichList.length">
                  </el-pagination>
                </el-tab-pane>

                <el-tab-pane label="Gene Expression" name="geneexpression">
                  <div class="search-bar">
                    <span>Select Gene:</span>
                    <input type="text" placeholder="CD3D" @input="inputInput" :value="inputText"/>
                    <button @click="handleSearch">Search</button>
                  </div>

                  <el-collapse v-model="activePanels">
                    <el-collapse-item v-show="showExpressionPanels" name="Plot" title="Expression">
                      <div id="geneUmapPlot" class="plot" style="height: 600px"></div>
                    </el-collapse-item>
                  </el-collapse>
                </el-tab-pane>

              </el-tabs>

            </div>
          </div>

        </div>
      </div>
    </section>

    <footer-component/>
  </div>
</template>

<style scoped>

/* ===== 更紧凑整体 ===== */
.main-section {
  padding: 100px 10px 20px 10px; /* 🔥 减左右空白 */
  background: #f5f7fb;
}

/* container再压缩 */
.custom-container {
  padding-left: 6px !important;
  padding-right: 6px !important;
}

/* 行间距控制（核心） */
.custom-row {
  --bs-gutter-x: 8px; /* 🔥 控制中间间距（默认是24px） */
}

/* 卡片更紧凑 */
.card {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

/* 搜索栏 */
.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  padding: 8px;
  border-radius: 8px;
  margin-bottom: 10px;
}

.search-bar input {
  flex: 1;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #ddd;
}

.search-bar button {
  background: #409eff;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
}

/* 图 */
.plot {
  height: 460px;
  background: #fafafa;
  border-radius: 8px;
}

/* Tabs */
:deep(.el-tabs__header) {
  background: #f8fafc;
}

:deep(.el-tabs__content) {
  padding: 8px;
}

/* 表格 */
:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
}

</style>